/**
 * Development Councils Translations (French)
 * @module developmentCouncils
 * @language fr
 */

export const developmentCouncils = {
  developmentCouncils: {
    title: "Conseils de Développement de JCI",
    subtitle: "Quatre Piliers Régionaux pour la Croissance & le Développement",
    description: "Les Conseils de Développement de Zone de JCI travaillent aux côtés du Conseil d'Administration de JCI et du Siège de JCI pour servir de piliers régionaux pour le développement du leadership, la croissance organisationnelle et l'impact durable à travers le monde.",

    heroBadge: "Expansion Mondiale de JCI",
    networkGrowthBadge: "Croissance du Réseau JCI",

    overview: {
      title: "Vue d'Ensemble Mondiale",
      description: "Ces conseils représentent les divisions géographiques stratégiques de JCI, travaillant ensemble pour développer des leaders pour un monde en mutation.",
      councilsCount: "4 Conseils de Zone",
      regionsServed: "Tous les Continents"
    },

    sections: {
      mission: "Mission",
      strategicGoals: "Objectifs Stratégiques 2025",
      keyAchievements: "Réalisations Clés",
      keyMetrics: "Métriques Clés",
      recommendations: "Recommandations 2026",
      highlights: "Points Forts 2025"
    },

    councils: {
      adc: {
        name: "Conseil de Développement Américain",
        abbreviation: "ADC",
        region: "Amérique",
        mission: "Mettre en valeur les actions qui augmentent la visibilité de la marque JCI, stimulent la croissance et la rétention des membres, et offrent des expériences de leadership de développement mondial aux membres.",
        goals: {
          brandAwareness: {
            name: "Notoriété de Marque et Visibilité",
            description: "Augmenter l'engagement sur les plateformes de médias sociaux et promouvoir les activités de JCI.",
            achievements: [
              "30% d'augmentation de l'engagement sur les réseaux sociaux",
              "2 articles publiés dans JCI Lead Magazine",
              "Tags stratégiques et interactions améliorées"
            ]
          },
          leadershipDevelopment: {
            name: "Développement du Leadership",
            description: "Former et développer de nouveaux leaders dans toute la région des Amériques.",
            achievements: [
              "Réunion des Présidents Nationaux avec 68% de participation (17 Présidents)",
              "Académie de Leadership Américaine 25e anniversaire avec 77% de participation",
              "27 nouveaux leaders formés sur plus de 30 heures de contenu",
              "Programme de Formation au Leadership avec 8 sessions"
            ]
          },
          sustainableStructure: {
            name: "Structure Ouverte, Efficace et Durable",
            description: "Soutenir le développement et l'expansion des Organisations Nationales.",
            achievements: [
              "Chili présenté comme Organisation Potentielle de JCI",
              "Guatemala soutenu vers le statut Provisoire",
              "Conférence du Club Junior avec 250 inscriptions"
            ]
          }
        },
        metrics: {
          socialMedia: { label: "Croissance Réseaux Sociaux", value: "30%" },
          membership: { label: "Objectif d'Adhésion", value: "5%" },
          bestPractices: { label: "Enquête Meilleures Pratiques", value: "834" },
          leadersTrained: { label: "Leaders Formés", value: "27" }
        },
        highlights: [
          "L'Académie de Leadership Américaine a célébré son 25e anniversaire",
          "Premier Événement Mondial en Ligne sur le Partage des Meilleures Pratiques",
          "Lancement de la Conférence du Club Junior avec le leadership ADC"
        ]
      },
      edc: {
        name: "Conseil de Développement Européen",
        abbreviation: "EDC",
        region: "Europe",
        mission: "Favoriser une culture soutenant la croissance, l'innovation et la collaboration entre les Organisations Nationales dans 41 régions d'Europe.",
        goals: {
          valueProposition: {
            name: "Proposition de Valeur",
            description: "Créer des campagnes trimestrielles sur les réseaux sociaux ciblant la démographie 18-40 ans.",
            achievements: [
              "+8 896 nouveaux abonnés de janvier à novembre",
              "9 789 publications effectuées",
              "5 176 partages réalisés"
            ]
          },
          geographicExpansion: {
            name: "Expansion Géographique",
            description: "Établir la présence de JCI dans de nouvelles régions.",
            achievements: [
              "Objectif : 3 nouvelles régions géographiques d'ici Q3",
              "Soutien aux nouvelles organisations potentielles",
              "Expansion au sein des Organisations Nationales existantes"
            ]
          },
          knowledgeSharing: {
            name: "Partage des Connaissances",
            description: "Organiser des programmes permettant la collaboration croisée.",
            achievements: [
              "4 programmes de partage des connaissances organisés",
              "Programme de mentorat renouvelé avec collaboration ASE",
              "24 équipes de mentorés inscrites"
            ]
          },
          productDevelopment: {
            name: "Développement de Nouveaux Produits",
            description: "Lancer des outils pour développer des chapitres locaux durables.",
            achievements: [
              "JCI Serious Game développé et testé",
              "7 pays ont participé aux tests de prototype",
              "Score de satisfaction moyen de 4,5/5"
            ]
          }
        },
        metrics: {
          followers: { label: "Nouveaux Abonnés", value: "+8 896" },
          academyParticipants: { label: "Participants à l'Académie", value: "158+" },
          gameSatisfaction: { label: "Satisfaction du Jeu", value: "4,5/5" },
          regionsCovered: { label: "Régions Couvertes", value: "41" }
        },
        highlights: [
          "Prototype du JCI Serious Game testé dans 7 pays",
          "Académie de Leadership Méditerranéenne (31 participants)",
          "Académie Européenne en Suède (99 participants)",
          "Académie Tri Nations (Irlande, Royaume-Uni, Écosse)",
          "Académie des Balkans (7 pays, 14 participants)"
        ]
      },
      amdec: {
        name: "Conseil de Développement Afrique et Moyen-Orient",
        abbreviation: "AMDEC",
        region: "Afrique et le Moyen-Orient",
        mission: "Autonomiser les jeunes leaders entrepreneurs en Afrique et au Moyen-Orient pour impulser un changement positif à travers le développement du leadership, les partenariats stratégiques et les solutions innovantes.",
        goals: {
          anniversary: {
            name: "Célébrations du 40e Anniversaire",
            description: "Marquer quatre décennies d'impact et de leadership dans toute la région.",
            achievements: [
              "Campagne numérique 40 Ans d'Impact lancée",
              "Cérémonies de reconnaissance pour les leaders passés et actuels",
              "Initiatives lancées pour soutenir l'héritage de l'AMDEC"
            ]
          },
          skillsDevelopment: {
            name: "Formation et Développement des Compétences",
            description: "Mettre en œuvre des formations mensuelles sur les domaines opérationnels clés.",
            achievements: [
              "Formation en Gestion Financière et Budgets",
              "Réunions Efficaces et Procédures Parlementaires",
              "Gestion de Projet et Évaluation d'Impact",
              "Image de Marque Personnelle et Développement du Leadership"
            ]
          },
          strategicCollaboration: {
            name: "Collaboration Stratégique",
            description: "Surmonter les barrières linguistiques et favoriser la collaboration régionale.",
            achievements: [
              "Série JCI Lusophone lancée pour les pays lusophones",
              "Conférence du Club Junior avec 100+ participants en ligne",
              "Série de Jeux JCI en partenariat avec EDC et JCI France"
            ]
          }
        },
        metrics: {
          yearsOfImpact: { label: "Années d'Impact", value: "40" },
          newMarkets: { label: "Nouveaux Marchés", value: "2" },
          conferenceAttendees: { label: "Participants à la Conférence", value: "100+" },
          trainingTopics: { label: "Sujets de Formation", value: "4+" }
        },
        highlights: [
          "Célébrations du 40e Anniversaire dans toute la région",
          "Réunion des Présidents Nationaux à Maurice",
          "27e Académie JCI Afrique et Moyen-Orient",
          "Série JCI Lusophone reliant les nations lusophones",
          "Le Kenya a complété l'étape d'enregistrement gouvernemental"
        ]
      },
      apdc: {
        name: "Conseil de Développement Asie-Pacifique",
        abbreviation: "APDC",
        region: "Asie et le Pacifique",
        mission: "Étendre la présence de JCI et agir comme un facteur positif dans le développement régional à travers la mission de JCI.",
        goals: {
          strategicPlan: {
            name: "Exécution du Plan Stratégique",
            description: "Partager et exécuter le Cadre d'Action JCI et le Plan Stratégique 2023-2027.",
            achievements: [
              "12 missions menées vers les pays cibles",
              "Collaboration avec d'autres Conseils de Développement",
              "Plan structuré à travers des réunions trimestrielles"
            ]
          },
          countryEmpowerment: {
            name: "Autonomisation des Pays",
            description: "Autonomiser les pays assignés vers l'affiliation ou la graduation.",
            achievements: [
              "6 nouvelles Organisations Locales affiliées",
              "1 Organisation Nationale déclarée",
              "356+ membres recrutés",
              "Fondations durables en administration et finances"
            ]
          },
          countryDevelopment: {
            name: "Développement Spécifique par Pays",
            description: "Programmes de développement ciblés pour chaque pays.",
            achievements: [
              "Thaïlande : 20% de croissance des adhésions (150→180)",
              "Myanmar : Collecte de 11 000 USD pour l'aide aux séismes",
              "Sri Lanka : Première Convention Nationale après la relance",
              "Ouzbékistan : Croissance de 46 à 60 membres"
            ]
          }
        },
        metrics: {
          membersRecruited: { label: "Membres Recrutés", value: "356+" },
          newLocalOrgs: { label: "Nouvelles Orgs Locales", value: "6" },
          missionsConducted: { label: "Missions Menées", value: "12" },
          nationalOrgs: { label: "Orgs Nationales", value: "1" }
        },
        highlights: [
          "JCI Thaïlande a atteint 85% de l'Indice de Performance de Durabilité",
          "Projet d'Aide aux Séismes du Myanmar a aidé 500+ personnes",
          "Sri Lanka a tenu sa première Convention Nationale",
          "Timor-Leste connecté avec 9 organisations partenaires",
          "Développement du Laos initié avec le partenariat YEAL"
        ]
      }
    },

    comparativeAnalysis: {
      title: "Analyse Comparative",
      subtitle: "Comment les quatre conseils se comparent sur les dimensions clés",
      categories: {
        geographicCoverage: {
          name: "Couverture Géographique",
          adc: "Amérique Centrale et du Sud, Caraïbes, Amérique du Nord",
          edc: "41 régions, concentrées dans les centres universitaires",
          amdec: "40+ ans de présence régionale",
          apdc: "Myanmar, Thaïlande, Sri Lanka, Timor-Leste, Ouzbékistan, Laos"
        },
        strategicPriorities: {
          name: "Priorités Stratégiques",
          adc: "Visibilité de la marque via les réseaux sociaux, objectifs de croissance des adhésions",
          edc: "Expansion géographique, partage des connaissances, produits innovants",
          amdec: "Héritage institutionnel, structures fondamentales, nouveaux marchés",
          apdc: "Autonomisation des pays, missions ciblées, renforcement des capacités"
        },
        leadershipDevelopment: {
          name: "Développement du Leadership",
          adc: "Réunions des Présidents Nationaux, Académie de Leadership Américaine",
          edc: "Académies régionales, programmes de mentorat, jeux sérieux",
          amdec: "Académies de Leadership, formations mensuelles, mentorat",
          apdc: "Missions spécifiques par pays, ateliers spécialisés"
        }
      }
    },

    keyHighlights: {
      title: "Points Forts 2025",
      subtitle: "Principales réalisations et innovations de tous les conseils",
      items: {
        seriousGame: {
          title: "JCI Serious Game",
          description: "Outil d'apprentissage interactif développé par EDC, testé dans 7 pays avec une note de satisfaction de 4,5/5."
        },
        juniorClub: {
          title: "Conférence du Club Junior",
          description: "Initiative inter-conseils avec 250+ inscriptions, inspirant les jeunes aspirants JCI."
        },
        anniversary: {
          title: "40e Anniversaire de l'AMDEC",
          description: "Quatre décennies d'impact célébrées avec des campagnes numériques et des cérémonies de reconnaissance."
        },
        apdcGrowth: {
          title: "Jalon de Croissance APDC",
          description: "356+ membres recrutés, 6 nouvelles Organisations Locales affiliées, 12 missions stratégiques menées."
        },
        lusophoneSeries: {
          title: "Série JCI Lusophone",
          description: "Reliant les pays lusophones dont l'Angola, le Cap-Vert, la Guinée-Bissau et plus."
        }
      }
    },

    newNationalOrganizations: {
      title: "Nouvelles Organisations Nationales",
      subtitle: "Développer notre réseau mondial avec des communautés JCI émergentes",
      potential: {
        title: "Potentiel",
        description: "Explorer l'affiliation JCI et établir des structures fondamentales"
      },
      provisional: {
        title: "Provisoire",
        description: "Développer l'adhésion et répondre aux normes JCI pour l'affiliation complète"
      },
      fullAffiliation: {
        title: "Affiliation Complète",
        description: "A récemment obtenu le statut de membre à part entière du réseau mondial JCI"
      },
      countries: {
        angola: "JCI Angola",
        djibouti: "JCI Djibouti",
        chile: "JCI Chili",
        gambia: "JCI Gambie",
        sriLanka: "JCI Sri Lanka",
        portugal: "JCI Portugal",
        myanmar: "JCI Myanmar",
        armenia: "JCI Arménie"
      }
    },

    chairperson: "Président",
    learnMore: "En Savoir Plus",
    viewDetails: "Voir les Détails",
    source: "Source : Rapport Complet des Conseils de Développement JCI 2025"
  }
} as const;
